import type { Preset, ExtraNote } from '../types'

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })
  if (!res.ok) {
    let message = res.statusText
    try {
      const body = await res.json()
      message = body.error || message
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

// --- Auth ---

export async function login(pin: string): Promise<void> {
  await request('/api/auth/login', { method: 'POST', body: JSON.stringify({ pin }) })
}

export async function logout(): Promise<void> {
  await request('/api/auth/logout', { method: 'POST' })
}

export async function checkAuth(): Promise<boolean> {
  try {
    await request('/api/auth/check')
    return true
  } catch {
    return false
  }
}

// --- Gammes (presets) ---

export function getGammes(): Promise<Preset[]> {
  return request<Preset[]>('/api/gammes')
}

export function saveGamme(preset: {
  name: string
  noteOffsets: number[]
  referencePitch: number
  tonicNote?: number
  tonicFrequency?: number
  extraNotes?: ExtraNote[]
}): Promise<Preset> {
  return request<Preset>('/api/gammes', { method: 'POST', body: JSON.stringify(preset) })
}

export function deleteGamme(id: number): Promise<void> {
  return request(`/api/gammes/${id}`, { method: 'DELETE' })
}

export function importGammesBulk(
  presets: Preset[]
): Promise<{ imported: number; skipped: string[] }> {
  return request('/api/gammes/import-bulk', { method: 'POST', body: JSON.stringify(presets) })
}

// --- Live tuning state ---

export interface TuningStateDto {
  noteOffsets: number[]
  referencePitch: number
  tonicNote: number
  tonicFrequency: number
  extraNotes: ExtraNote[]
}

export function getTuningState(): Promise<TuningStateDto> {
  return request<TuningStateDto>('/api/state')
}

export function putTuningState(state: TuningStateDto): Promise<void> {
  return request('/api/state', { method: 'PUT', body: JSON.stringify(state) })
}
